import React, { useState, useEffect, useRef } from "react";
import { motion } from "framer-motion";
import { 
  Send, 
  Loader2, 
  Cpu, 
  Monitor, 
  HardDrive, 
  Zap,
  RefreshCw,
  ChevronDown,
  Sparkles
} from "lucide-react";
import axios from "axios";
import ReactMarkdown from "react-markdown";
import MainLayout from "../components/MainLayout";
import SEO, { pageSEO } from "../components/SEO";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Stable markdown components for ReactMarkdown (defined outside component to prevent re-creation)
const markdownComponents = {
  h1: ({children}) => <h1 className="text-xl font-bold text-white mb-3 mt-4 first:mt-0">{children}</h1>,
  h2: ({children}) => <h2 className="text-lg font-bold text-blue-400 mb-2 mt-4 first:mt-0">{children}</h2>,
  h3: ({children}) => <h3 className="text-base font-semibold text-cyan-400 mb-2 mt-3">{children}</h3>,
  p: ({children}) => <p className="mb-2 last:mb-0 leading-relaxed">{children}</p>,
  ul: ({children}) => <ul className="list-disc list-inside mb-2 space-y-1">{children}</ul>,
  ol: ({children}) => <ol className="list-decimal list-inside mb-2 space-y-1">{children}</ol>,
  li: ({children}) => <li className="text-slate-300">{children}</li>,
  strong: ({children}) => <strong className="text-white font-semibold">{children}</strong>,
  code: ({children}) => <code className="bg-slate-700 px-1.5 py-0.5 rounded text-cyan-400 text-sm">{children}</code>,
  hr: () => <hr className="border-slate-700 my-4" />,
};

// Quick suggestion buttons for common queries
const quickSuggestions = [
  { label: "Gaming PC £800", query: "I want a gaming PC for around £800, 1080p 144Hz gaming" },
  { label: "Video Editing £1500", query: "I need a PC for video editing, budget around £1500" },
  { label: "Office PC £400", query: "I need a simple office PC for £400, just web browsing and documents" },
  { label: "Streaming Setup", query: "I want to stream on Twitch while gaming, budget flexible around £1200" },
];

const PCBuilderPage = () => {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [showSuggestions, setShowSuggestions] = useState(true);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Send initial greeting when page loads
  useEffect(() => {
    const sendInitialGreeting = async () => {
      setLoading(true);
      try {
        const res = await axios.post(`${API}/pc-builder/chat`, {
          message: "Hello, I want to build a PC",
          session_id: null
        });
        setSessionId(res.data.session_id);
        setMessages([{ role: "assistant", content: res.data.response }]);
      } catch (err) {
        setMessages([{ 
          role: "assistant", 
          content: "👋 Hello! I'm the SmartIT PC Builder Assistant. I'll help you design the perfect PC for your needs.\n\nTo get started, tell me:\n- **What will you use the PC for?** (gaming, work, video editing, etc.)\n- **What's your budget in GBP?**\n- **Any specific requirements?** (quiet, RGB, small case, etc.)" 
        }]);
      }
      setLoading(false);
    };
    
    sendInitialGreeting();
  }, []);

  const sendMessage = async (messageText) => {
    const userMessage = messageText || input.trim();
    if (!userMessage || loading) return;

    setInput("");
    setShowSuggestions(false);
    setMessages(prev => [...prev, { role: "user", content: userMessage }]);
    setLoading(true);

    try {
      const res = await axios.post(`${API}/pc-builder/chat`, {
        message: userMessage,
        session_id: sessionId
      });
      setSessionId(res.data.session_id);
      setMessages(prev => [...prev, { role: "assistant", content: res.data.response }]);
    } catch (err) {
      setMessages(prev => [...prev, { 
        role: "assistant", 
        content: "Sorry, I'm having trouble right now. Please try again or contact SmartIT at 07435324947 for PC building advice." 
      }]);
    }
    setLoading(false);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    sendMessage();
  };

  const handleQuickSuggestion = (query) => {
    sendMessage(query);
  };

  const startNewConversation = () => {
    setSessionId(null);
    setMessages([]);
    setShowSuggestions(true);
    // Re-trigger initial greeting
    const sendInitialGreeting = async () => {
      setLoading(true);
      try {
        const res = await axios.post(`${API}/pc-builder/chat`, {
          message: "Hello, I want to build a PC",
          session_id: null
        });
        setSessionId(res.data.session_id);
        setMessages([{ role: "assistant", content: res.data.response }]);
      } catch (err) {
        setMessages([{ 
          role: "assistant", 
          content: "👋 Hello! I'm the SmartIT PC Builder Assistant. I'll help you design the perfect PC for your needs.\n\nTo get started, tell me:\n- **What will you use the PC for?** (gaming, work, video editing, etc.)\n- **What's your budget in GBP?**\n- **Any specific requirements?** (quiet, RGB, small case, etc.)" 
        }]);
      }
      setLoading(false);
    };
    sendInitialGreeting();
  };

  return (
    <MainLayout>
      <SEO {...pageSEO.pcBuilder} />
      <div className="pt-4 pb-12" data-testid="pc-builder-page">
        <div className="max-w-5xl mx-auto px-4">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-8"
        >
          <div className="inline-flex items-center gap-2 bg-blue-500/10 border border-blue-500/30 rounded-full px-4 py-1.5 mb-4">
            <Sparkles className="w-4 h-4 text-blue-400" />
            <span className="text-blue-400 text-sm font-medium">AI-Powered</span>
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
            PC <span className="text-gradient">Builder</span>
          </h1>
          <p className="text-slate-400 max-w-2xl mx-auto">
            Tell me what you need, and I will design the perfect PC build for you. 
            Complete with compatibility checks, prices, and UK shopping links.
          </p>
        </motion.div>

        {/* Feature Pills */}
        <div className="flex flex-wrap justify-center gap-3 mb-8">
          <div className="flex items-center gap-2 bg-slate-900/60 border border-slate-800 rounded-full px-4 py-2">
            <Cpu className="w-4 h-4 text-cyan-400" />
            <span className="text-slate-300 text-sm">Compatible Parts</span>
          </div>
          <div className="flex items-center gap-2 bg-slate-900/60 border border-slate-800 rounded-full px-4 py-2">
            <Monitor className="w-4 h-4 text-green-400" />
            <span className="text-slate-300 text-sm">Performance Optimized</span>
          </div>
          <div className="flex items-center gap-2 bg-slate-900/60 border border-slate-800 rounded-full px-4 py-2">
            <HardDrive className="w-4 h-4 text-purple-400" />
            <span className="text-slate-300 text-sm">UK Pricing</span>
          </div>
          <div className="flex items-center gap-2 bg-slate-900/60 border border-slate-800 rounded-full px-4 py-2">
            <Zap className="w-4 h-4 text-yellow-400" />
            <span className="text-slate-300 text-sm">Upgrade Paths</span>
          </div>
        </div>

        {/* Chat Container */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-slate-900/60 border border-slate-800 rounded-2xl overflow-hidden shadow-2xl"
        >
          {/* Chat Header */}
          <div className="bg-gradient-to-r from-blue-600 to-cyan-600 p-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
                <Cpu className="w-6 h-6 text-white" />
              </div>
              <div>
                <p className="font-semibold text-white text-lg">PC Builder Assistant</p>
                <p className="text-sm text-blue-100">Powered by SmartIT AI</p>
              </div>
            </div>
            <button 
              onClick={startNewConversation}
              className="flex items-center gap-2 bg-white/10 hover:bg-white/20 text-white px-4 py-2 rounded-lg transition-colors"
              data-testid="new-conversation-btn"
            >
              <RefreshCw className="w-4 h-4" />
              <span className="hidden sm:inline">New Build</span>
            </button>
          </div>

          {/* Messages Area */}
          <div className="h-[500px] overflow-y-auto p-4 md:p-6 space-y-4" data-testid="chat-messages">
            {messages.map((msg, i) => (
              <motion.div 
                key={i} 
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className={`flex ${msg.role === "user" ? "justify-end" : "justify-start"}`}
              >
                <div className={`max-w-[90%] md:max-w-[80%] rounded-2xl px-4 py-3 ${
                  msg.role === "user" 
                    ? "bg-blue-600 text-white" 
                    : "bg-slate-800/80 text-slate-200 border border-slate-700/50"
                }`}>
                  {msg.role === "assistant" ? (
                    <div className="prose prose-invert prose-sm max-w-none">
                      <ReactMarkdown components={markdownComponents}>
                        {msg.content}
                      </ReactMarkdown>
                    </div>
                  ) : (
                    <p className="whitespace-pre-wrap">{msg.content}</p>
                  )}
                </div>
              </motion.div>
            ))}
            
            {loading && (
              <motion.div 
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="flex justify-start"
              >
                <div className="bg-slate-800/80 border border-slate-700/50 rounded-2xl px-4 py-3 flex items-center gap-2">
                  <Loader2 className="w-5 h-5 text-blue-400 animate-spin" />
                  <span className="text-slate-400 text-sm">Designing your build...</span>
                </div>
              </motion.div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Quick Suggestions */}
          {showSuggestions && messages.length <= 1 && (
            <div className="px-4 md:px-6 pb-4">
              <p className="text-slate-400 text-sm mb-3 flex items-center gap-2">
                <ChevronDown className="w-4 h-4" />
                Quick start options:
              </p>
              <div className="flex flex-wrap gap-2">
                {quickSuggestions.map((suggestion, i) => (
                  <button
                    key={i}
                    onClick={() => handleQuickSuggestion(suggestion.query)}
                    className="bg-slate-800 hover:bg-slate-700 border border-slate-700 hover:border-blue-500/50 text-slate-300 hover:text-white px-4 py-2 rounded-lg text-sm transition-all"
                    disabled={loading}
                    data-testid={`quick-suggestion-${i}`}
                  >
                    {suggestion.label}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Input Area */}
          <form onSubmit={handleSubmit} className="p-4 md:p-6 border-t border-slate-800 bg-slate-900/40">
            <div className="flex gap-3">
              <input
                ref={inputRef}
                type="text"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="Describe your ideal PC build..."
                className="flex-1 bg-slate-800 border border-slate-700 focus:border-blue-500 rounded-xl px-4 py-3 text-white placeholder-slate-500 outline-none transition-colors"
                disabled={loading}
                data-testid="pc-builder-input"
              />
              <button 
                type="submit" 
                className="bg-blue-600 hover:bg-blue-500 disabled:bg-slate-700 disabled:cursor-not-allowed text-white px-5 py-3 rounded-xl transition-colors flex items-center gap-2"
                disabled={loading || !input.trim()}
                data-testid="pc-builder-send"
              >
                {loading ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <Send className="w-5 h-5" />
                )}
              </button>
            </div>
          </form>
        </motion.div>

        {/* Info Cards */}
        <div className="grid md:grid-cols-3 gap-4 mt-8">
          <div className="bg-slate-900/60 border border-slate-800 rounded-xl p-5">
            <div className="w-10 h-10 bg-blue-500/10 rounded-lg flex items-center justify-center mb-3">
              <Cpu className="w-5 h-5 text-blue-400" />
            </div>
            <h3 className="text-white font-semibold mb-2">Compatibility Guaranteed</h3>
            <p className="text-slate-400 text-sm">Every build is checked for socket, RAM, and case compatibility.</p>
          </div>
          <div className="bg-slate-900/60 border border-slate-800 rounded-xl p-5">
            <div className="w-10 h-10 bg-green-500/10 rounded-lg flex items-center justify-center mb-3">
              <Monitor className="w-5 h-5 text-green-400" />
            </div>
            <h3 className="text-white font-semibold mb-2">UK Pricing</h3>
            <p className="text-slate-400 text-sm">All prices in GBP with links to UK retailers like Scan, Amazon UK, and more.</p>
          </div>
          <div className="bg-slate-900/60 border border-slate-800 rounded-xl p-5">
            <div className="w-10 h-10 bg-purple-500/10 rounded-lg flex items-center justify-center mb-3">
              <Zap className="w-5 h-5 text-purple-400" />
            </div>
            <h3 className="text-white font-semibold mb-2">Build Service</h3>
            <p className="text-slate-400 text-sm">SmartIT can build your PC for you! Contact us for assembly services.</p>
          </div>
        </div>

        {/* Contact CTA */}
        <div className="mt-8 text-center">
          <p className="text-slate-400 mb-4">
            Need help with your build? SmartIT offers PC building and upgrade services in Aylesbury.
          </p>
          <a 
            href="https://wa.me/447435324947?text=Hi%2C%20I%20need%20help%20building%20a%20PC" 
            target="_blank" 
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 bg-green-600 hover:bg-green-500 text-white font-semibold px-6 py-3 rounded-xl transition-colors"
          >
            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
              <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
            </svg>
            Contact SmartIT on WhatsApp
          </a>
        </div>
        </div>
      </div>
    </MainLayout>
  );
};

export default PCBuilderPage;
